import { createUuid } from '@notifi/uuid/browser';
import {
  duplicateMembershipBillingOptions,
  normalizeMembershipSaveOffers,
  type CreateMembershipPlanPayload,
  type MembershipPlan,
} from '@/components/organisms/pricebook/support/membership-plan-shared';
import type {
  PlanRecurringService,
  PlanRecurringServiceInput,
} from '@/hooks/membership-plans/usePlanRecurringServices';

export function buildDuplicateMembershipPlanPayload(
  plan: MembershipPlan,
  recurringServices: PlanRecurringService[] = [],
): CreateMembershipPlanPayload {
  const duplicatedServiceIds = new Map(
    recurringServices.map((service) => [service.id, createUuid()]),
  );
  const duplicatedRecurringServices = recurringServices.map((service) => ({
    ...duplicateRecurringServiceInput(service),
    id: duplicatedServiceIds.get(service.id)!,
  }));
  const deferredRevenueSettings =
    plan.revenueRecognitionMode !== 'PER_VISIT' || !plan.deferredRevenueSettings
      ? null
      : {
          ...plan.deferredRevenueSettings,
          serviceAllocations: plan.deferredRevenueSettings.serviceAllocations.map(
            (allocation) => {
              const recurringServiceId = duplicatedServiceIds.get(
                allocation.recurringServiceId,
              );
              if (!recurringServiceId) {
                throw new Error(
                  'This plan has a deferred-revenue allocation for a missing included visit.',
                );
              }
              return { ...allocation, recurringServiceId };
            },
          ),
        };
  return {
    name: `${plan.name} Copy`,
    description: plan.description || undefined,
    discountPercent: plan.discountPercent,
    priceAmount: plan.priceAmount,
    priceCurrency: plan.priceCurrency ?? undefined,
    billingIntervalValue: plan.billingIntervalValue,
    billingIntervalUnit: plan.billingIntervalUnit,
    eligibleLineItemTypes: plan.eligibleLineItemTypes?.length
      ? plan.eligibleLineItemTypes
      : undefined,
    eligibleLineItemIds: plan.eligibleLineItemIds?.length ? plan.eligibleLineItemIds : undefined,
    excludedLineItemIds: plan.excludedLineItemIds?.length ? plan.excludedLineItemIds : undefined,
    priority: plan.priority,
    setupFee: plan.setupFee,
    contractLengthMonths: plan.contractLengthMonths,
    billingOptions: duplicateMembershipBillingOptions(plan.billingOptions),
    coverageRules: plan.coverageRules?.length ? plan.coverageRules : undefined,
    loadedLaborRate: plan.loadedLaborRate,
    costUtilizationPercent: plan.costUtilizationPercent,
    revenueRecognitionMode: plan.revenueRecognitionMode ?? undefined,
    visitRecognitionPercent:
      plan.revenueRecognitionMode === 'VISIT_LINKED' ? (plan.visitRecognitionPercent ?? 60) : null,
    deferredRevenueSettings,
    recurringServices:
      duplicatedRecurringServices.length > 0 ? duplicatedRecurringServices : undefined,
    discountTiers: plan.discountTiers?.length ? plan.discountTiers : undefined,
    coveredServices: plan.coveredServices?.length
      ? plan.coveredServices.map((service) => ({
          lineItemId: service.lineItemId,
          name: service.name,
          quantityPerPeriod: service.quantityPerPeriod,
          entitlementPeriodType: service.entitlementPeriodType,
          rolloverPolicy: service.rolloverPolicy ?? 'EXPIRE',
          rolloverMax:
            (service.rolloverPolicy ?? 'EXPIRE') === 'ROLLOVER_LIMITED'
              ? (service.rolloverMax ?? null)
              : null,
          noShowPolicy: service.noShowPolicy ?? 'PRESERVE',
          reschedulePolicy: service.reschedulePolicy ?? 'PRESERVE',
          seasonalWindow: service.seasonalWindow ?? null,
          equipmentCategory: service.equipmentCategory ?? null,
          jobTypeId: service.jobTypeId ?? null,
          firstOfferOffsetMonths: service.firstOfferOffsetMonths ?? null,
          kind: service.kind,
        }))
      : undefined,
    waivers: plan.waivers?.length ? plan.waivers : undefined,
    renewalRules: plan.renewalRules ?? undefined,
    cancellationRules: plan.cancellationRules ?? undefined,
    saveOfferTemplates: plan.saveOfferTemplates?.length
      ? normalizeMembershipSaveOffers(plan.saveOfferTemplates, plan.priceCurrency)
      : undefined,
    display: plan.display?.valuePoints?.length
      ? { valuePoints: plan.display.valuePoints }
      : undefined,
  };
}

export function duplicateRecurringServiceInput(
  service: PlanRecurringService,
): PlanRecurringServiceInput {
  return {
    label: service.label,
    jobTypeId: service.jobTypeId,
    businessUnitId: service.businessUnitId,
    invoiceTemplateItemId: service.invoiceTemplateItemId,
    priority: service.priority,
    jobSummary: service.jobSummary,
    recurrenceType: service.recurrenceType,
    recurrenceInterval: service.recurrenceInterval,
    recurrenceMonths: service.recurrenceMonths,
    durationType: service.durationType,
    totalVisits: service.totalVisits,
    firstVisitComplete: service.firstVisitComplete,
    coverageId: service.coverageId,
    attachTo: service.attachTo,
    scaling: service.scaling,
    extraUnitMinutes: service.extraUnitMinutes,
  };
}
